/*==================================================
  HERSHOP — FEATURED NEW ARRIVALS
  Vanilla JS Slider Engine
  No external libraries. Production ready.

  v2: "closed door" accordion cards.
  Every product card is a real flex item. Only the
  active card (wide) and its open neighbours (narrow,
  "closed") ever have a non-zero width — every other
  card is collapsed to width:0 so it takes no visual
  space at all. Flex `order` (based on the shortest
  circular distance to the active index) keeps the
  correct sibling positioned on the correct side even
  across the wrap-around point, so the infinite loop
  never produces a visible jump.

  v3: adds a "paper flip" transition (transform + opacity
  keyframes on the inner wrapper only, GPU-composited).
  On every navigation, the card losing focus gets
  .is-leaving and the card gaining focus gets
  .is-entering; direction (left/right) is written to
  --hershop-flip-dir on the stage so the same two
  keyframes work for both next() and prev(). See the
  CSS file for the actual animation.
==================================================*/

(function () {
  'use strict';

  var TRANSITION_MS = 750; // must match CSS width transition duration
  var FLIP_ANIM_MS = 500;  // must match CSS .is-entering/.is-leaving keyframe duration
  var SWIPE_THRESHOLD = 50; // px
  var RESIZE_SETTLE_MS = 150;
  var OPEN_SIDE_COUNT = 4; // closed "door" cards visible on each side of the active card

  /* ------------------------------------------------
     Shortest circular distance between two indices
     within a looped collection of `length` items.
  ------------------------------------------------ */
  function shortestDiff(index, activeIndex, length) {
    var diff = index - activeIndex;
    if (length === 0) return diff;
    diff = diff - Math.round(diff / length) * length;
    return diff;
  }

  function mod(n, m) {
    return ((n % m) + m) % m;
  }

  /* ------------------------------------------------
     HershopArrivals — one instance per section
  ------------------------------------------------ */
  function HershopArrivals(root) {
    this.root = root;
    this.stage = root.querySelector('[data-hershop-stage]');
    this.track = root.querySelector('[data-hershop-track]');
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-hershop-card]'));
    this.dotsWrap = root.querySelector('[data-hershop-dots]');
    this.prevBtn = root.querySelector('[data-hershop-prev]');
    this.nextBtn = root.querySelector('[data-hershop-next]');

    this.length = this.cards.length;
    this.openSideCount = Math.min(OPEN_SIDE_COUNT, Math.floor((this.length - 1) / 2));

    if (!this.length || !this.stage || !this.track) {
      return;
    }

    this.activeIndex = 0;
    this.isAnimating = false;
    this.isInView = false;

    this.dots = [];

    this.resizeTimer = null;
    this.animationTimer = null;
    this.flipAnimTimer = null;

    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchDeltaX = 0;
    this.isTouching = false;
    this.isHorizontalSwipe = null;

    this._bindMethods();
    this._buildDots();
    this._bindEvents();
    this._observeVisibility();
    this.render(false);
  }

  HershopArrivals.prototype._bindMethods = function () {
    this.next = this.next.bind(this);
    this.prev = this.prev.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onCardClick = this.onCardClick.bind(this);
  };

  /* ------------------------------------------------
     Build pagination dots (used mainly on mobile)
  ------------------------------------------------ */
  HershopArrivals.prototype._buildDots = function () {
    if (!this.dotsWrap) return;

    this.dotsWrap.innerHTML = '';
    this.dots = [];

    for (var i = 0; i < this.length; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'hershop-arrivals-dot';
      dot.setAttribute('aria-label', 'Go to product ' + (i + 1));

      (function (self, index) {
        dot.addEventListener('click', function () {
          self.goTo(index);
        });
      })(this, i);

      this.dotsWrap.appendChild(dot);
      this.dots.push(dot);
    }
  };

  /* ------------------------------------------------
     Which "band" a card falls into relative to a given
     active index: active / open (closed-door side card,
     within OPEN_SIDE_COUNT of active) / hidden (collapsed).
  ------------------------------------------------ */
  HershopArrivals.prototype._bandOf = function (i, fromIndex) {
    var d = Math.abs(shortestDiff(i, fromIndex, this.length));
    if (d === 0) return 'active';
    if (d <= this.openSideCount) return 'open';
    return 'hidden';
  };

  /* ------------------------------------------------
     Apply state (order + width class) to every card
     based on its circular distance from the active
     index. CSS transitions (width/opacity) handle the
     actual animation — this function only sets the
     end state.
  ------------------------------------------------ */
  HershopArrivals.prototype.render = function (animate) {
    var self = this;

    var apply = function () {
      for (var i = 0; i < self.cards.length; i++) {
        var card = self.cards[i];
        var diff = shortestDiff(i, self.activeIndex, self.length);
        var band = self._bandOf(i, self.activeIndex);

        card.style.order = String(diff);
        card.classList.remove('is-active', 'is-open', 'is-hidden');

        if (band === 'active') {
          card.classList.add('is-active');
          card.style.pointerEvents = 'auto';
          card.setAttribute('aria-hidden', 'false');
          card.tabIndex = 0;
        } else if (band === 'open') {
          card.classList.add('is-open');
          card.style.pointerEvents = 'auto';
          card.setAttribute('aria-hidden', 'true');
          card.tabIndex = -1;
        } else {
          card.classList.add('is-hidden');
          card.style.pointerEvents = 'none';
          card.setAttribute('aria-hidden', 'true');
          card.tabIndex = -1;
        }
      }

      for (var d = 0; d < self.dots.length; d++) {
        self.dots[d].classList.toggle('is-active', d === self.activeIndex);
      }

      self._updateArrowState();
    };

    if (animate) {
      window.requestAnimationFrame(apply);
    } else {
      // First paint / resize settle: skip transitions entirely
      // to avoid a visible "fly-in" animation or flicker.
      this.track.classList.add('is-static');
      apply();
      void this.track.offsetWidth; // force reflow
      this.track.classList.remove('is-static');
    }
  };

  HershopArrivals.prototype._updateArrowState = function () {
    // Infinite loop: arrows stay enabled at all times.
    if (this.prevBtn) this.prevBtn.disabled = false;
    if (this.nextBtn) this.nextBtn.disabled = false;
  };

  /* ------------------------------------------------
     Navigation
  ------------------------------------------------ */
  HershopArrivals.prototype.next = function () {
    this.goTo(mod(this.activeIndex + 1, this.length));
  };

  HershopArrivals.prototype.prev = function () {
    this.goTo(mod(this.activeIndex - 1, this.length));
  };

  HershopArrivals.prototype.goTo = function (index) {
    if (this.isAnimating) return;
    if (index === this.activeIndex) return;

    this.isAnimating = true;

    var self = this;
    var oldActiveIndex = this.activeIndex;
    var newActiveIndex = mod(index, this.length);

    // Snapshot which band every card is in before the move, so we
    // can tell afterwards exactly which cards' widths actually
    // change. Only those get will-change — with 4 open slots per
    // side that's usually a handful of cards, not all 9.
    var animatingCards = [];
    for (var i = 0; i < this.cards.length; i++) {
      var before = this._bandOf(i, oldActiveIndex);
      var after = this._bandOf(i, newActiveIndex);
      if (before !== after) {
        animatingCards.push(this.cards[i]);
        this.cards[i].classList.add('is-animating');
      }
    }

    var previousActiveCard = this.cards[oldActiveIndex];

    this.activeIndex = newActiveIndex;
    var activeCard = this.cards[this.activeIndex];

    // Direction of travel (shortest circular path) decides which way
    // the "page" flips. Written once to the stage as a CSS variable
    // so the same pair of keyframes (out/in) works for both next()
    // and prev(), and for any goTo() jump (dots, side-card clicks).
    var dirDiff = shortestDiff(newActiveIndex, oldActiveIndex, this.length);
    var flipDir = dirDiff < 0 ? -1 : 1;
    this.stage.style.setProperty('--hershop-flip-dir', flipDir);

    // Paper-flip: the outgoing active card flips away, the incoming
    // one flips in. Classes are removed + reflowed first so repeated
    // quick navigation always restarts the keyframe animation cleanly.
    if (previousActiveCard) {
      previousActiveCard.classList.remove('is-leaving');
      void previousActiveCard.offsetWidth; // force reflow
      previousActiveCard.classList.add('is-leaving');
    }
    if (activeCard) {
      activeCard.classList.remove('is-entering');
      void activeCard.offsetWidth; // force reflow
      activeCard.classList.add('is-entering');
    }

    this.render(true);

    var clearLock = function () {
      self.isAnimating = false;
      for (var j = 0; j < animatingCards.length; j++) {
        animatingCards[j].classList.remove('is-animating');
      }
      if (activeCard) {
        activeCard.removeEventListener('transitionend', onTransitionEnd);
      }
      if (self.animationTimer) {
        clearTimeout(self.animationTimer);
        self.animationTimer = null;
      }
    };

    function onTransitionEnd(e) {
      if (e.target !== activeCard) return;
      if (e.propertyName !== 'width') return;
      clearLock();
    }

    if (activeCard) {
      activeCard.addEventListener('transitionend', onTransitionEnd);
    }

    // Fallback in case transitionend does not fire (e.g. tab hidden).
    this.animationTimer = setTimeout(clearLock, TRANSITION_MS + 150);

    // Flip-class cleanup runs on its own (shorter) timer, independent
    // of the width-transition lock above.
    if (this.flipAnimTimer) clearTimeout(this.flipAnimTimer);
    this.flipAnimTimer = setTimeout(function () {
      if (previousActiveCard) previousActiveCard.classList.remove('is-leaving');
      if (activeCard) activeCard.classList.remove('is-entering');
    }, FLIP_ANIM_MS + 50);
  };

  /* ------------------------------------------------
     Card click: closed side cards (is-open) recenter
     instead of navigating to the product immediately.
     The active card still navigates normally.
  ------------------------------------------------ */
  HershopArrivals.prototype.onCardClick = function (e) {
    var card = e.currentTarget;
    var index = parseInt(card.getAttribute('data-index'), 10);

    if (index !== this.activeIndex) {
      e.preventDefault();
      this.goTo(index);
    }
  };

  /* ------------------------------------------------
     Keyboard navigation (only while section is visible)
  ------------------------------------------------ */
  HershopArrivals.prototype.onKeydown = function (e) {
    if (!this.isInView) return;

    var tag = document.activeElement ? document.activeElement.tagName : '';
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.next();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.prev();
    }
  };

  /* ------------------------------------------------
     Touch / swipe support
  ------------------------------------------------ */
  HershopArrivals.prototype.onTouchStart = function (e) {
    var touch = e.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchDeltaX = 0;
    this.isTouching = true;
    this.isHorizontalSwipe = null;
  };

  HershopArrivals.prototype.onTouchMove = function (e) {
    if (!this.isTouching) return;

    var touch = e.touches[0];
    var deltaX = touch.clientX - this.touchStartX;
    var deltaY = touch.clientY - this.touchStartY;

    if (this.isHorizontalSwipe === null) {
      this.isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    }

    if (this.isHorizontalSwipe) {
      this.touchDeltaX = deltaX;
      if (e.cancelable) e.preventDefault();
    }
  };

  HershopArrivals.prototype.onTouchEnd = function () {
    if (!this.isTouching) return;
    this.isTouching = false;

    if (this.isHorizontalSwipe && Math.abs(this.touchDeltaX) > SWIPE_THRESHOLD) {
      if (this.touchDeltaX < 0) {
        this.next();
      } else {
        this.prev();
      }
    }

    this.touchDeltaX = 0;
    this.isHorizontalSwipe = null;
  };

  /* ------------------------------------------------
     Resize handling — CSS clamp()/vw values already
     handle the responsive sizing on their own; all we
     need here is to briefly kill transitions so a
     breakpoint change never animates into place.
  ------------------------------------------------ */
  HershopArrivals.prototype.onResize = function () {
    var self = this;
    if (this.resizeTimer) clearTimeout(this.resizeTimer);

    this.resizeTimer = setTimeout(function () {
      self.render(false);
    }, RESIZE_SETTLE_MS);
  };

  /* ------------------------------------------------
     Visibility tracking for keyboard nav scoping
  ------------------------------------------------ */
  HershopArrivals.prototype._observeVisibility = function () {
    var self = this;

    if (!('IntersectionObserver' in window)) {
      self.isInView = true;
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          self.isInView = entry.isIntersecting;
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(this.root);
    this._intersectionObserver = observer;
  };

  /* ------------------------------------------------
     Event binding
  ------------------------------------------------ */
  HershopArrivals.prototype._bindEvents = function () {
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.next);
    if (this.prevBtn) this.prevBtn.addEventListener('click', this.prev);

    for (var i = 0; i < this.cards.length; i++) {
      this.cards[i].addEventListener('click', this.onCardClick);
    }

    document.addEventListener('keydown', this.onKeydown);

    this.stage.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.stage.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.stage.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.stage.addEventListener('touchcancel', this.onTouchEnd, { passive: true });

    window.addEventListener('resize', this.onResize);
  };

  HershopArrivals.prototype.destroy = function () {
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.next);
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.prev);

    for (var i = 0; i < this.cards.length; i++) {
      this.cards[i].removeEventListener('click', this.onCardClick);
    }

    document.removeEventListener('keydown', this.onKeydown);

    this.stage.removeEventListener('touchstart', this.onTouchStart);
    this.stage.removeEventListener('touchmove', this.onTouchMove);
    this.stage.removeEventListener('touchend', this.onTouchEnd);
    this.stage.removeEventListener('touchcancel', this.onTouchEnd);

    window.removeEventListener('resize', this.onResize);

    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
    }

    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
    }

    if (this.flipAnimTimer) {
      clearTimeout(this.flipAnimTimer);
    }

    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
  };

  /* ------------------------------------------------
     Instance registry (supports multiple sections
     on the same page + Shopify Theme Editor events)
  ------------------------------------------------ */
  var instances = {};

  function initSection(root) {
    var id = root.getAttribute('data-section-id') || root.id;
    if (!id) return;

    if (instances[id]) {
      instances[id].destroy();
      delete instances[id];
    }

    instances[id] = new HershopArrivals(root);
  }

  function initAll() {
    var sections = document.querySelectorAll('[data-hershop-arrivals]');
    for (var i = 0; i < sections.length; i++) {
      initSection(sections[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Shopify Theme Editor support */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-hershop-arrivals]');
    if (root) initSection(root);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-hershop-arrivals]');
    if (!root) return;
    var id = root.getAttribute('data-section-id') || root.id;
    if (instances[id]) {
      instances[id].destroy();
      delete instances[id];
    }
  });
})();